'use client'

import { Check, ClipboardCopy, FlaskConical, RotateCcw, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { LabSlider } from './lab-slider'
import { BASE_RAMP, FONT_OPTIONS } from './theme-lab-tokens'
import { useThemeLabStore } from './use-theme-lab-store'

const fixedCornerStyle = {
  right: 'max(calc(1rem + 8px), env(safe-area-inset-right))',
  bottom: 'max((calc(1rem + 60px)), env(safe-area-inset-bottom))',
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-muted-foreground text-[11px] font-semibold tracking-widest uppercase">
        {title}
      </h3>
      {children}
    </section>
  )
}

function ThemeLabFab() {
  const setOpen = useThemeLabStore((s) => s.setOpen)

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label="Open Theme Lab"
      className="corner-squircle bg-card text-muted-foreground hover:text-foreground fixed z-100 grid size-10 place-items-center rounded-xl border shadow-sm"
      style={fixedCornerStyle}
    >
      <FlaskConical className="size-4" strokeWidth={1.75} />
    </button>
  )
}

function BrandSection() {
  const brand = useThemeLabStore((s) => s.brand)
  const setBrand = useThemeLabStore((s) => s.setBrand)

  return (
    <Section title="Brand">
      <div className="flex items-center gap-3">
        <div
          className="corner-squircle size-9 shrink-0 rounded-lg border"
          style={{
            background: `oklch(${brand.l} ${brand.c} ${brand.h})`,
          }}
        />
        <div className="flex-1 space-y-2">
          <LabSlider
            label="Lightness"
            value={brand.l}
            min={0.2}
            max={0.99}
            step={0.01}
            onChange={(l) => setBrand({ l })}
          />
          <LabSlider
            label="Chroma"
            value={brand.c}
            min={0}
            max={0.37}
            step={0.005}
            decimals={3}
            onChange={(c) => setBrand({ c })}
          />
          <LabSlider
            label="Hue"
            value={brand.h}
            min={0}
            max={360}
            step={1}
            decimals={0}
            onChange={(h) => setBrand({ h })}
          />
        </div>
      </div>
    </Section>
  )
}

function RampSection() {
  const rampHue = useThemeLabStore((s) => s.rampHue)
  const chromaMul = useThemeLabStore((s) => s.chromaMul)
  const patch = useThemeLabStore((s) => s.patch)

  return (
    <Section title="Neutral ramp">
      <div className="flex gap-px overflow-hidden rounded-md border">
        {BASE_RAMP.map(({ step, l, c }) => (
          <div
            key={step}
            className="h-5 flex-1"
            style={{
              background: `oklch(${l} ${c * chromaMul} ${rampHue})`,
            }}
            title={`base-${step}`}
          />
        ))}
      </div>
      <LabSlider
        label="Hue"
        value={rampHue}
        min={0}
        max={360}
        step={1}
        decimals={0}
        onChange={(nextRampHue) => patch({ rampHue: nextRampHue })}
      />
      <LabSlider
        label="Chroma x"
        value={chromaMul}
        min={0.2}
        max={3}
        step={0.05}
        onChange={(nextChromaMul) => patch({ chromaMul: nextChromaMul })}
      />
    </Section>
  )
}

function ShapeSection() {
  const radius = useThemeLabStore((s) => s.radius)
  const displayWeight = useThemeLabStore((s) => s.displayWeight)
  const patch = useThemeLabStore((s) => s.patch)

  return (
    <Section title="Shape & weight">
      <LabSlider
        label="Radius"
        value={radius}
        min={0}
        max={1.5}
        step={0.025}
        decimals={3}
        suffix="rem"
        onChange={(nextRadius) => patch({ radius: nextRadius })}
      />
      <LabSlider
        label="Display weight"
        value={displayWeight}
        min={100}
        max={900}
        step={100}
        decimals={0}
        onChange={(nextDisplayWeight) =>
          patch({ displayWeight: nextDisplayWeight })
        }
      />
    </Section>
  )
}

function TypeSection() {
  const fontSans = useThemeLabStore((s) => s.fontSans)
  const fontHeading = useThemeLabStore((s) => s.fontHeading)
  const patch = useThemeLabStore((s) => s.patch)

  return (
    <Section title="Type">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Sans (body / UI)</Label>
        <Select
          value={fontSans}
          onValueChange={(nextFontSans) => patch({ fontSans: nextFontSans })}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FONT_OPTIONS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="text-xs"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Heading</Label>
        <Select
          value={fontHeading}
          onValueChange={(nextFontHeading) =>
            patch({ fontHeading: nextFontHeading })
          }
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FONT_OPTIONS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="text-xs"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </Section>
  )
}

function CopyFooter() {
  const exportBlock = useThemeLabStore((s) => s.exportBlock)
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const copyTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(exportBlock())
      setCopyError(null)
      setCopied(true)

      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current)
      }

      copyTimeoutRef.current = window.setTimeout(() => {
        setCopied(false)
        copyTimeoutRef.current = null
      }, 2000)
    } catch {
      setCopied(false)
      setCopyError('Clipboard unavailable')
    }
  }

  return (
    <div className="border-t p-3">
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={handleCopy}
      >
        {copied ? (
          <Check className="size-3.5" />
        ) : (
          <ClipboardCopy className="size-3.5" />
        )}
        {copied ? 'Copied' : "Copy [data-theme='lab'] block"}
      </Button>
      {copyError ? (
        <p className="text-destructive mt-2 text-xs">{copyError}</p>
      ) : null}
    </div>
  )
}

export function ThemeLabPanel() {
  const open = useThemeLabStore((s) => s.open)
  const reset = useThemeLabStore((s) => s.reset)
  const setOpen = useThemeLabStore((s) => s.setOpen)

  if (!open) {
    return <ThemeLabFab />
  }

  return (
    <div
      className="corner-squircle bg-card fixed z-100 flex max-h-[80dvh] w-80 flex-col rounded-xl border shadow-lg"
      style={fixedCornerStyle}
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <FlaskConical
            className="text-muted-foreground size-4"
            strokeWidth={1.75}
          />
          <span className="text-sm font-semibold">Theme Lab</span>
          <span className="text-muted-foreground text-[10px] tracking-widest uppercase">
            dev
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={reset}
            title="Reset"
            aria-label="Reset Theme Lab"
          >
            <RotateCcw className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => setOpen(false)}
            aria-label="Close Theme Lab"
          >
            <X className="size-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 scrollbar-thin space-y-6 overflow-y-auto px-4 py-4">
        <BrandSection />
        <RampSection />
        <ShapeSection />
        <TypeSection />
      </div>

      <CopyFooter />
    </div>
  )
}
